/**
 * ExpenseForm Component
 * Complete expense creation/editing form with validation
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CurrencyInput } from './CurrencyInput';
import { CategorySelector, Category } from './CategorySelector';
import { DateRangePicker, DateRange } from './DateRangePicker';
import { FileUploadZone } from './FileUploadZone';

export interface ExpenseFormData {
  category_id: string;
  amount: number;
  currency: string;
  description: string;
  expense_date: string;
  merchant_name?: string;
  business_purpose?: string;
  is_billable: boolean;
  project_code?: string;
  receipt_urls?: string[];
  manager_id?: string | null;
}

export interface ExpenseFormProps {
  initialData?: Partial<ExpenseFormData>;
  onSubmit: (data: ExpenseFormData, action: 'draft' | 'submit') => void | Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  submitting?: boolean;
  className?: string;
  categories?: Category[];
  submitButtonText?: string;
  showSaveAsDraft?: boolean;
  showSubmitForApproval?: boolean;
  // Optional: supply a handler to upload files and return accessible URLs
  onUploadReceiptFiles?: (files: File[]) => Promise<string[]>;
  // Optional: indicate whether a receipt is required for validation hinting
  requiresReceipt?: boolean;
  // Optional: list of managers to choose from
  managers?: { id: string; name: string; email?: string }[];
  currentUserId?: string;
  currentUserEmail?: string;
  // Optional: refresh managers callback
  onRefreshManagers?: () => void | Promise<void>;
}

const defaultFormData: ExpenseFormData = {
  category_id: '',
  amount: 0,
  currency: 'USD',
  description: '',
  expense_date: '',
  merchant_name: '',
  business_purpose: '',
  is_billable: false,
  project_code: '',
  receipt_urls: [],
  manager_id: null,
};

export function ExpenseForm({
  initialData = {},
  onSubmit,
  onCancel,
  loading = false,
  submitting = false,
  className,
  categories = [],
  managers = [],
  currentUserId,
  currentUserEmail,
  submitButtonText = 'Save Expense',
  onUploadReceiptFiles,
  requiresReceipt = false,
  showSaveAsDraft = true,
  showSubmitForApproval = false,
  onRefreshManagers,
}: ExpenseFormProps) {
  const [formData, setFormData] = useState<ExpenseFormData>({
    ...defaultFormData,
    ...initialData,
  });
  
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [managerInlineError, setManagerInlineError] = useState<string | undefined>(undefined);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  // Merge external initialData updates (e.g., autofill from receipt extraction)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ExpenseFormData, string>> = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    if (!formData.expense_date) {
      newErrors.expense_date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData, 'submit');
    }
  };

  const handleFieldChange = <K extends keyof ExpenseFormData>(
    field: K,
    value: ExpenseFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleDateChange = (range: DateRange) => {
    handleFieldChange('expense_date', range.startDate ? range.startDate.toISOString().split('T')[0] : '');
  };

  return (
    <Card className={cn('w-full max-w-2xl mx-auto relative', className)}>
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-gray-900">
          {initialData.description ? 'Edit Expense' : 'Create New Expense'}
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {(loading || submitting) && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10" aria-live="polite">
            <div className="flex items-center gap-3 text-gray-700">
              <span className="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <span>{submitting ? 'Saving expense…' : 'Loading…'}</span>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6" aria-busy={loading || submitting}>
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input
                  label="Description *"
                  placeholder="Enter expense description"
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  error={errors.description}
                  disabled={loading || submitting}
                />
              </div>
              
              <CurrencyInput
                label="Amount *"
                value={formData.amount || undefined}
                onChange={(value) => handleFieldChange('amount', value || 0)}
                currency={formData.currency}
                error={errors.amount}
                disabled={loading || submitting}
                minValue={0.01}
                maxValue={50000}
              />
              
              <CategorySelector
                label="Category *"
                categories={categories || []}
                value={formData.category_id || undefined}
                onChange={(categoryId) => handleFieldChange('category_id', categoryId || '')}
                error={errors.category_id}
                disabled={loading || submitting}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateRangePicker
                label="Expense Date *"
                value={{ 
                  startDate: formData.expense_date ? new Date(formData.expense_date) : null, 
                  endDate: formData.expense_date ? new Date(formData.expense_date) : null 
                }}
                onChange={handleDateChange}
                error={errors.expense_date}
                disabled={loading || submitting}
                maxDate={new Date()}
              />
              
              <Input
                label="Merchant/Vendor"
                placeholder="Enter merchant name"
                value={formData.merchant_name || ''}
                onChange={(e) => handleFieldChange('merchant_name', e.target.value)}
                disabled={loading || submitting}
              />
            </div>

            {/* Optional Manager Selection */}
            {Array.isArray(managers) && managers.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Assign Manager (optional)</label>
                    {typeof onRefreshManagers === 'function' && (
                      <button
                        type="button"
                        className="text-xs text-primary-700 hover:underline disabled:opacity-50"
                        onClick={() => onRefreshManagers?.()}
                        disabled={loading || submitting}
                        aria-label="Refresh managers"
                      >
                        Refresh
                      </button>
                    )}
                  </div>
                  <select
                    className={cn(
                      'w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
                      'focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600',
                      'disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                    )}
                    value={formData.manager_id || ''}
                    onChange={(e) => {
                      const val = e.target.value || null;
                      if (val && currentUserId && val === currentUserId) {
                        // Prevent selecting self; show inline guidance
                        setManagerInlineError('You cannot assign yourself as your manager. Please choose a different manager or leave it blank.');
                        handleFieldChange('manager_id', null);
                        return;
                      }
                      setManagerInlineError(undefined);
                      handleFieldChange('manager_id', val);
                    }}
                    disabled={loading || submitting}
                  >
                    <option value="">— No Manager Selected —</option>
                    {managers.map((m) => {
                      const isSelf = (currentUserId && m.id === currentUserId) || (currentUserEmail && m.email && m.email === currentUserEmail);
                      return (
                        <option key={m.id} value={m.id} disabled={!!isSelf}>
                          {m.name}{m.email ? ` (${m.email})` : ''}{isSelf ? ' (You)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">If selected, your expense will route to this manager for Level-1 approval.</p>
                  {managerInlineError && (
                    <p className="text-xs text-error-600 mt-1">{managerInlineError}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Additional Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
              Additional Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Project Code"
                  placeholder="Enter project code (optional)"
                  value={formData.project_code || ''}
                  onChange={(e) => handleFieldChange('project_code', e.target.value)}
                  disabled={loading || submitting}
                />
              </div>
              
              <div className="flex items-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_billable}
                    onChange={(e) => handleFieldChange('is_billable', e.target.checked)}
                    disabled={loading || submitting}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Billable to client</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Purpose
              </label>
              <textarea
                rows={3}
                placeholder="Describe the business purpose of this expense"
                value={formData.business_purpose || ''}
                onChange={(e) => handleFieldChange('business_purpose', e.target.value)}
                disabled={loading || submitting}
                className={cn(
                  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600',
                  'disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50',
                  'placeholder:text-gray-400'
                )}
              />
            </div>
          </div>

          {/* Receipt Upload */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
              Receipt & Documentation
            </h3>
            
            {/* Optional dropzone for uploading receipts */}
            {onUploadReceiptFiles && (
              <div className="space-y-2">
                <FileUploadZone
                  label="Attach Receipt Files"
                  helperText="Drag & drop or click to select receipt files (images/PDFs)."
                  onFilesSelected={(files) => {
                    setSelectedFiles(files);
                    setUploadError(undefined);
                  }}
                  disabled={loading || submitting || uploading}
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {selectedFiles.length > 0
                      ? `${selectedFiles.length} file(s) selected`
                      : 'No files selected'}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!selectedFiles.length || uploading || loading || submitting}
                    onClick={async () => {
                      try {
                        setUploading(true);
                        setUploadError(undefined);
                        if (!onUploadReceiptFiles) {
                          setUploadError('Uploading is not available in this context. Please use the receipt upload section above.');
                          return;
                        }
                        const urls = await onUploadReceiptFiles(selectedFiles);
                        if (Array.isArray(urls) && urls.length) {
                          // Deduplicate and merge
                          const merged = Array.from(new Set([...(formData.receipt_urls || []), ...urls.filter(Boolean)]));
                          setFormData(prev => ({ ...prev, receipt_urls: merged }));
                          setSelectedFiles([]);
                        }
                      } catch (e: any) {
                        setUploadError(e?.message || 'Failed to upload receipt files');
                      } finally {
                        setUploading(false);
                      }
                    }}
                  >
                    {uploading ? 'Uploading…' : 'Upload Selected Files'}
                  </Button>
                </div>
                {uploadError && (
                  <p className="text-sm text-error-600">{uploadError}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt URLs (Optional)
              </label>
              <Input
                placeholder="Enter receipt URLs separated by commas"
                value={formData.receipt_urls?.join(', ') || ''}
                onChange={(e) => {
                  const urls = e.target.value.split(',').map(url => url.trim()).filter(url => url);
                  handleFieldChange('receipt_urls', urls);
                }}
                disabled={loading || submitting}
                helperText="Enter URLs of uploaded receipts, separated by commas"
              />
              {requiresReceipt && (!formData.receipt_urls || formData.receipt_urls.length === 0) && (
                <p className="mt-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                  A receipt is required for this category. Please upload or add a receipt URL.
                </p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={loading || submitting}
              >
                Cancel
              </Button>
            )}
            
            {showSaveAsDraft && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  // Save as draft - still validate required fields for data integrity
                  if (validateForm()) {
                    onSubmit(formData, 'draft');
                  }
                }}
                disabled={loading || submitting}
              >
                Save as Draft
              </Button>
            )}
            
            <Button
              type="submit"
              loading={submitting}
              disabled={loading || submitting}
            >
              {submitButtonText}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
